import React, { useMemo, useState } from 'react';
import {
    AlertTriangle, CheckCircle2, ChevronDown, ChevronRight, Info, Loader2, Lock, UserCheck, UserPlus, Users, X,
} from 'lucide-react';
import { ADRESSAT_LABEL } from './fragenkatalog';
import {
    empfaengerVonHand,
    gleicherEmpfaenger,
    pruefeEmpfaengerEingabe,
    vorschlagEmpfaenger,
    type EmpfaengerEintrag,
    type EmpfaengerQuellen,
    type EmpfaengerSpeicherstand,
    type EmpfaengerVorschlag,
    type EmpfaengerZuordnung as Zuordnung,
} from './empfaenger';
import type { FrageAdressat } from './types';

/**
 * Der Abschnitt „Empfaenger je Adressat" oberhalb der Fragenliste (Plan
 * Stufe 2.3). Je Adressat, der in den Fragen vorkommt: die zugeordnete
 * Person, die Vorschlaege zum Uebernehmen, eine Eingabe von Hand, Entfernen.
 *
 * Reine Anzeige — Zustand und Speichern liegen in `useEmpfaengerZuordnung`,
 * die Regeln in `empfaenger.ts`. Nichts wird automatisch gesetzt; ein
 * Vorschlag ist ein Knopf. In der Nur-Ansicht wird nur gelesen.
 */

interface Props {
    adressaten: FrageAdressat[];
    zuordnung: Zuordnung;
    quellen?: EmpfaengerQuellen | null;
    isReadOnly: boolean;
    laden: boolean;
    /** Spalte/Tabelle fehlt oder Lesen gescheitert — sperrt jeden Schreibweg. */
    hinweis: string | null;
    /** Konflikt-Meldung: jemand anderes hat denselben Adressaten geaendert. */
    meldung: string | null;
    stand: EmpfaengerSpeicherstand;
    fehler: string | null;
    /** Alle Schreibwege (Setzen, Entfernen, Erneut speichern) frei? (`darfSchreiben`) */
    schreibbar: boolean;
    onSetzen: (adressat: FrageAdressat, eintrag: EmpfaengerEintrag | null) => void;
    onErneutSpeichern: () => void;
    onMeldungSchliessen: () => void;
}

const QUELLE_LABEL: Record<EmpfaengerEintrag['quelle'], string> = {
    kontakt: 'aus dem Verteiler',
    bauherr: 'aus den Projektdaten',
    hand: 'von Hand',
};

const Speicheranzeige: React.FC<{ stand: EmpfaengerSpeicherstand; nurLesen: boolean; laden: boolean }> = ({ stand, nurLesen, laden }) => {
    if (nurLesen) {
        return (
            <span className="inline-flex items-center gap-1.5 text-[11px] font-bold text-slate-400 shrink-0">
                <Lock className="w-3.5 h-3.5" /> Nur lesend
            </span>
        );
    }
    if (laden) {
        return (
            <span className="inline-flex items-center gap-1.5 text-[11px] font-bold text-slate-400 shrink-0">
                <Loader2 className="w-3.5 h-3.5 animate-spin" /> Lädt …
            </span>
        );
    }
    if (stand === 'fehler') {
        return (
            <span className="inline-flex items-center gap-1.5 text-[11px] font-bold text-rose-600 shrink-0">
                <AlertTriangle className="w-3.5 h-3.5" /> Nicht gespeichert
            </span>
        );
    }
    if (stand === 'speichert') {
        return (
            <span className="inline-flex items-center gap-1.5 text-[11px] font-bold text-blue-500 shrink-0">
                <Loader2 className="w-3.5 h-3.5 animate-spin" /> Speichert …
            </span>
        );
    }
    return (
        <span className="inline-flex items-center gap-1.5 text-[11px] font-bold text-emerald-600 shrink-0">
            <CheckCircle2 className="w-3.5 h-3.5" /> Gespeichert
        </span>
    );
};

/** Eingabe von Hand — Name Pflicht, E-Mail freiwillig; geprueft in `pruefeEmpfaengerEingabe`. */
const HandEingabe: React.FC<{
    vorgabe: EmpfaengerEintrag | null;
    onUebernehmen: (eintrag: EmpfaengerEintrag) => void;
    onAbbrechen: () => void;
}> = ({ vorgabe, onUebernehmen, onAbbrechen }) => {
    const [name, setName] = useState(vorgabe ? vorgabe.name : '');
    const [email, setEmail] = useState(vorgabe && vorgabe.email ? vorgabe.email : '');
    const [meldung, setMeldung] = useState<string | null>(null);

    const uebernehmen = () => {
        const problem = pruefeEmpfaengerEingabe(name, email);
        if (problem) { setMeldung(problem); return; }
        onUebernehmen(empfaengerVonHand(name, email));
    };

    return (
        <div className="mt-2 flex flex-col sm:flex-row sm:items-start gap-2">
            <input
                type="text"
                value={name}
                onChange={e => { setName(e.target.value); setMeldung(null); }}
                onKeyDown={e => { if (e.key === 'Enter') uebernehmen(); }}
                placeholder="Name (Pflicht)"
                className="flex-1 min-w-0 px-3 py-1.5 border border-slate-200 rounded-lg text-xs"
                aria-label="Name des Empfängers"
            />
            <input
                type="email"
                value={email}
                onChange={e => { setEmail(e.target.value); setMeldung(null); }}
                onKeyDown={e => { if (e.key === 'Enter') uebernehmen(); }}
                placeholder="E-Mail (optional)"
                className="flex-1 min-w-0 px-3 py-1.5 border border-slate-200 rounded-lg text-xs"
                aria-label="E-Mail des Empfängers"
            />
            <div className="flex items-center gap-2 shrink-0">
                <button
                    type="button"
                    onClick={uebernehmen}
                    className="px-3 py-1.5 rounded-lg text-xs font-bold bg-blue-600 text-white hover:bg-blue-700"
                >
                    Übernehmen
                </button>
                <button type="button" onClick={onAbbrechen} className="text-xs font-bold text-slate-500 hover:text-slate-700">
                    Abbrechen
                </button>
            </div>
            {meldung && <span className="text-[11px] text-rose-600 sm:basis-full">{meldung}</span>}
        </div>
    );
};

const AdressatZeile: React.FC<{
    adressat: FrageAdressat;
    eintrag: EmpfaengerEintrag | null;
    vorschlaege: EmpfaengerVorschlag[];
    isReadOnly: boolean;
    schreibbar: boolean;
    onSetzen: (eintrag: EmpfaengerEintrag | null) => void;
}> = ({ adressat, eintrag, vorschlaege, isReadOnly, schreibbar, onSetzen }) => {
    const [handOffen, setHandOffen] = useState(false);
    // Der schon zugeordnete Vorschlag wird nicht noch einmal angeboten.
    const offen = vorschlaege.filter(v => !gleicherEmpfaenger(v.eintrag, eintrag));

    return (
        <div className="px-4 py-3 border-b border-slate-100 last:border-0">
            <div className="flex items-start gap-3 flex-wrap">
                <span className="shrink-0 w-11 text-[10px] font-bold px-1.5 py-0.5 rounded bg-slate-100 text-slate-600 border border-slate-200 text-center">
                    {adressat}
                </span>
                <div className="flex-1 min-w-0">
                    <div className="text-[11px] font-black text-slate-400 uppercase tracking-wider leading-tight">
                        {ADRESSAT_LABEL[adressat]}
                    </div>
                    {eintrag ? (
                        <div className="flex items-center gap-2 flex-wrap mt-1">
                            <UserCheck className="w-3.5 h-3.5 text-emerald-600 shrink-0" />
                            <span className="text-sm text-slate-800 font-semibold break-words">{eintrag.name}</span>
                            {eintrag.email && <span className="text-xs text-slate-500 break-all">{eintrag.email}</span>}
                            <span className="text-[10px] text-slate-400">{QUELLE_LABEL[eintrag.quelle]}</span>
                            {!isReadOnly && (
                                <button
                                    type="button"
                                    onClick={() => onSetzen(null)}
                                    disabled={!schreibbar}
                                    className="inline-flex items-center gap-1 text-[11px] font-bold text-slate-500 hover:text-rose-600 disabled:opacity-40"
                                    title="Zuordnung entfernen"
                                >
                                    <X className="w-3.5 h-3.5" /> Entfernen
                                </button>
                            )}
                        </div>
                    ) : (
                        <div className="text-xs text-slate-400 mt-1">Noch niemand zugeordnet.</div>
                    )}

                    {!isReadOnly && (offen.length > 0 || !handOffen) && (
                        <div className="flex items-center gap-2 flex-wrap mt-2">
                            {offen.map(v => (
                                <button
                                    key={`${v.eintrag.kontaktId || ''}|${v.eintrag.name}|${v.eintrag.email || ''}`}
                                    type="button"
                                    onClick={() => onSetzen(v.eintrag)}
                                    disabled={!schreibbar}
                                    title={`${v.grund}${v.eintrag.email ? ` · ${v.eintrag.email}` : ''}`}
                                    className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-bold text-blue-700 bg-blue-50 border border-blue-200 hover:bg-blue-100 disabled:opacity-40 max-w-full"
                                >
                                    <UserPlus className="w-3.5 h-3.5 shrink-0" />
                                    <span className="truncate">{v.eintrag.name}</span>
                                    <span className="font-normal text-blue-500 truncate hidden sm:inline">— {v.grund}</span>
                                </button>
                            ))}
                            {!handOffen && (
                                <button
                                    type="button"
                                    onClick={() => setHandOffen(true)}
                                    disabled={!schreibbar}
                                    className="text-xs font-bold text-slate-600 hover:text-slate-800 disabled:opacity-40"
                                >
                                    {eintrag ? 'Ändern …' : 'Von Hand eintragen …'}
                                </button>
                            )}
                        </div>
                    )}
                    {!isReadOnly && handOffen && (
                        <HandEingabe
                            vorgabe={eintrag}
                            onUebernehmen={e => { onSetzen(e); setHandOffen(false); }}
                            onAbbrechen={() => setHandOffen(false)}
                        />
                    )}
                </div>
            </div>
        </div>
    );
};

export const EmpfaengerZuordnung: React.FC<Props> = ({
    adressaten, zuordnung, quellen, isReadOnly, laden, hinweis, meldung, stand, fehler, schreibbar,
    onSetzen, onErneutSpeichern, onMeldungSchliessen,
}) => {
    const [zu, setZu] = useState(false);
    const vorschlaege = useMemo(() => {
        const je: Partial<Record<FrageAdressat, EmpfaengerVorschlag[]>> = {};
        adressaten.forEach(a => { je[a] = vorschlagEmpfaenger(a, quellen); });
        return je;
    }, [adressaten, quellen]);

    if (!adressaten.length) return null;
    const zugeordnet = adressaten.filter(a => !!zuordnung[a]).length;

    return (
        <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
            <button
                type="button"
                onClick={() => setZu(z => !z)}
                className="w-full flex items-center gap-3 px-4 py-3 bg-slate-50 border-b border-slate-200 text-left hover:bg-slate-100 transition-colors"
                aria-expanded={!zu}
            >
                {zu ? <ChevronRight className="w-4 h-4 text-slate-400" /> : <ChevronDown className="w-4 h-4 text-slate-400" />}
                <Users className="w-4 h-4 text-slate-500 shrink-0" />
                <span className="font-bold text-slate-800 text-sm flex-1 min-w-0">Empfänger je Adressat</span>
                <span className="text-xs text-slate-400 shrink-0">{zugeordnet} von {adressaten.length} zugeordnet</span>
                <Speicheranzeige stand={stand} nurLesen={isReadOnly} laden={laden} />
            </button>

            {!zu && (
                <>
                    <p className="px-4 py-2 text-[11px] text-slate-500 italic bg-slate-50/50 border-b border-slate-100">
                        Wer bekommt die Fragen dieser Rolle? Vorschläge kommen aus den Projektdaten und dem Verteiler —
                        gesetzt wird nur per Klick. Beim Versand werden Name und E-Mail in die Fragen übernommen.
                    </p>
                    {hinweis && (
                        <div className="mx-4 my-3 bg-amber-50 border border-amber-200 rounded-xl px-4 py-3 text-sm text-amber-900 flex items-start gap-2">
                            <Info className="w-4 h-4 shrink-0 mt-0.5 text-amber-600" />
                            <span className="flex-1 min-w-0">{hinweis}</span>
                        </div>
                    )}
                    {meldung && (
                        <div className="mx-4 my-3 bg-amber-50 border border-amber-200 rounded-xl px-4 py-3 text-sm text-amber-900 flex items-start gap-2">
                            <Info className="w-4 h-4 shrink-0 mt-0.5 text-amber-600" />
                            <span className="flex-1 min-w-0">{meldung}</span>
                            <button type="button" onClick={onMeldungSchliessen} className="text-xs font-bold underline shrink-0">
                                Schließen
                            </button>
                        </div>
                    )}
                    {fehler && (
                        <div className="mx-4 my-3 bg-rose-50 border border-rose-200 rounded-xl px-4 py-3 text-sm text-rose-800 flex items-start gap-2 flex-wrap">
                            <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
                            <span className="flex-1 min-w-0">{fehler}</span>
                            {/* Dieselben Riegel wie beim Setzen: nicht vor bekanntem
                                Serverstand, nicht ohne Spalte, nicht in der Nur-Ansicht. */}
                            <button
                                type="button"
                                onClick={onErneutSpeichern}
                                disabled={!schreibbar || stand === 'speichert'}
                                className="text-xs font-bold underline shrink-0 disabled:opacity-40"
                            >
                                Erneut speichern
                            </button>
                        </div>
                    )}
                    {adressaten.map(a => (
                        <AdressatZeile
                            key={a}
                            adressat={a}
                            eintrag={zuordnung[a] || null}
                            vorschlaege={vorschlaege[a] || []}
                            isReadOnly={isReadOnly}
                            schreibbar={schreibbar}
                            onSetzen={e => onSetzen(a, e)}
                        />
                    ))}
                </>
            )}
        </div>
    );
};
